import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { API_URL } from "@/config/api";
import { getShops } from "@/state/shops/shop.slice";
import { selectableShops } from "@/lib/shopScope";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { Storefront } from "@phosphor-icons/react";

/** Small coloured quantity chip, matching the products list badge. */
const QtyBadge = ({ quantity, alert = 5 }) => {
    const q = Number(quantity) || 0;
    const cls =
        q === 0
            ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
            : q <= alert
              ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
              : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300";
    return (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${cls}`}>
            {q}
        </span>
    );
};

/**
 * The single stock-adjustment modal used everywhere (products list, product
 * detail, edit). Self-sufficient: it reads shops + the signed-in user from the
 * store, works out which shop the adjustment targets, and fetches the product's
 * per-shop quantities so you can see what's on hand at each store before
 * adjusting.
 *
 * Stock is per-shop, so an adjustment always hits exactly ONE shop. When the
 * caller passes a [shopFilter] (the products page's shop filter) it locks to
 * that; otherwise a privileged "all shops" user picks one, and a scoped user is
 * locked to their own shop — mirroring the server.
 *
 * onSubmit(productId, signedAdjustment, reason, shopId) — the caller performs
 * the PATCH and refreshes its own data.
 */
export default function StockAdjustmentDialog({
    product,
    open,
    onClose,
    onSubmit,
    isLoading,
    shopFilter = "all",
}) {
    const dispatch = useDispatch();
    const { shops = [] } = useSelector((s) => s.shop || {});
    const user = useSelector((s) => s.auth?.user);
    const shopScope = selectableShops(shops || [], user);

    const [adjustment, setAdjustment] = useState(0);
    const [reason, setReason] = useState("");
    const [adjustmentType, setAdjustmentType] = useState("add");
    const [pickedShop, setPickedShop] = useState("");
    const [breakdown, setBreakdown] = useState(null);
    const [loadingBreakdown, setLoadingBreakdown] = useState(false);

    const lockedShopId =
        shopFilter && shopFilter !== "all"
            ? String(shopFilter)
            : !shopScope.allowAll
              ? String(shopScope.defaultShopId || "")
              : "";
    const needsPicker = !lockedShopId; // privileged user with "All Shops"
    const effectiveShopId = lockedShopId || pickedShop || "";
    const targetShopName =
        shops.find((s) => String(s.id) === String(effectiveShopId))?.name ||
        null;

    // Load shops if the store doesn't have them yet.
    useEffect(() => {
        if (open && (!shops || shops.length === 0)) dispatch(getShops());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Reset the form and fetch per-shop quantities each time it opens.
    useEffect(() => {
        if (!open || !product?.id) return;
        setAdjustment(0);
        setReason("");
        setAdjustmentType("add");
        setPickedShop("");
        setLoadingBreakdown(true);
        axios
            .get(`${API_URL}products/${product.id}`, { withCredentials: true })
            .then((res) => setBreakdown(res.data?.data?.stockBreakdown || []))
            .catch(() => setBreakdown([]))
            .finally(() => setLoadingBreakdown(false));
    }, [open, product?.id]);

    // The server returns every shop; only show the ones this user may see.
    const visibleBreakdown = (breakdown || []).filter(
        (b) =>
            shopScope.allowAll ||
            b.shopId == null ||
            (shopScope.shops || []).some((s) => Number(s.id) === Number(b.shopId))
    );
    const currentAtTarget = effectiveShopId
        ? visibleBreakdown.find(
              (b) => String(b.shopId) === String(effectiveShopId)
          )?.quantity ?? 0
        : null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const finalAdjustment =
            adjustmentType === "add"
                ? Math.abs(adjustment)
                : -Math.abs(adjustment);
        onSubmit(product.id, finalAdjustment, reason, effectiveShopId || null);
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Adjust Stock — {product?.title}</DialogTitle>
                    <DialogDescription>
                        {targetShopName
                            ? `Adjusting stock at ${targetShopName}.`
                            : "Select the shop to adjust."}
                        {currentAtTarget != null &&
                            ` Currently ${currentAtTarget} units there.`}
                    </DialogDescription>
                </DialogHeader>

                {/* Per-shop quantities so you can see what's on hand before adjusting. */}
                <div className="rounded-md border bg-muted/30">
                    <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b flex items-center gap-1.5">
                        <Storefront className="h-3.5 w-3.5" weight="bold" />
                        Stock by shop
                    </div>
                    {loadingBreakdown ? (
                        <div className="px-3 py-3 text-xs text-muted-foreground flex items-center gap-2">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Loading…
                        </div>
                    ) : visibleBreakdown.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-muted-foreground">
                            No stock records yet.
                        </div>
                    ) : (
                        <div className="max-h-40 overflow-y-auto divide-y">
                            {visibleBreakdown.map((b) => {
                                const isTarget =
                                    String(b.shopId) ===
                                    String(effectiveShopId);
                                return (
                                    <div
                                        key={b.stockId ?? `s-${b.shopId}`}
                                        className={`flex items-center justify-between px-3 py-1.5 text-sm ${
                                            isTarget ? "bg-primary/5" : ""
                                        }`}
                                    >
                                        <span
                                            className={
                                                isTarget ? "font-semibold" : ""
                                            }
                                        >
                                            {b.shopName}
                                            {isTarget && (
                                                <span className="ml-1 text-[10px] uppercase tracking-wide text-primary">
                                                    target
                                                </span>
                                            )}
                                        </span>
                                        <QtyBadge quantity={b.quantity} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 pt-1">
                    {needsPicker && (
                        <div className="space-y-2">
                            <Label>Shop</Label>
                            <Select
                                value={pickedShop}
                                onValueChange={setPickedShop}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a shop" />
                                </SelectTrigger>
                                <SelectContent>
                                    {(shopScope.shops || shops).map((s) => (
                                        <SelectItem
                                            key={s.id}
                                            value={String(s.id)}
                                        >
                                            {s.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label>Adjustment Type</Label>
                        <Select
                            value={adjustmentType}
                            onValueChange={setAdjustmentType}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="add">Add Stock</SelectItem>
                                <SelectItem value="remove">
                                    Remove Stock
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="adjustment">Quantity</Label>
                        <Input
                            id="adjustment"
                            type="number"
                            min="1"
                            value={adjustment}
                            onChange={(e) =>
                                setAdjustment(parseInt(e.target.value) || 0)
                            }
                            placeholder="Enter quantity"
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="reason">Reason (Optional)</Label>
                        <Input
                            id="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. New shipment, Damaged goods"
                        />
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={
                                isLoading ||
                                adjustment <= 0 ||
                                (needsPicker && !pickedShop)
                            }
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Adjusting…
                                </>
                            ) : (
                                `${adjustmentType === "add" ? "Add" : "Remove"} Stock`
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
